import { t } from "@lingui/macro";
import { DownloadSimple, List, SquaresFour } from "@phosphor-icons/react";
import { Button, ScrollArea, Tabs, TabsContent, TabsList, TabsTrigger } from "@reactive-resume/ui";
import {
  BILLING_INTERVALS,
  type BillingInterval,
  PLAN_TIERS,
  type PlanTier,
} from "@reactive-resume/utils";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";

import { toast } from "@/client/hooks/use-toast";
import { trackRegisterSubscribe } from "@/client/libs/analytics";
import { queryClient } from "@/client/libs/query-client";
import { useResumes } from "@/client/services/resume";
import { fetchUser, useUser } from "@/client/services/user";
import { useAuthStore } from "@/client/stores/auth";
import { useBillingStore } from "@/client/stores/billing";
import { useDialog } from "@/client/stores/dialog";

import { GridView } from "./_layouts/grid";
import { ListView } from "./_layouts/list";

type Layout = "grid" | "list";

const BILLING_POLL_ATTEMPTS = 8;
const BILLING_POLL_INTERVAL_MS = 2500;
const PAYWALL_AUTO_SHOWN_KEY = "careerbldr:paywall-auto-shown";

async function pollForActivePlan(): Promise<{
  id: string;
  planTier: NonNullable<PlanTier>;
  email: string | null;
  stripeSubscriptionId: string | null;
} | null> {
  for (let i = 0; i < BILLING_POLL_ATTEMPTS; i++) {
    if (i > 0) await new Promise((r) => setTimeout(r, BILLING_POLL_INTERVAL_MS));

    const user = await queryClient.fetchQuery({
      queryKey: ["user"],
      queryFn: fetchUser,
      staleTime: 0,
    });

    if (user?.planTier) {
      useAuthStore.getState().setUser(user);
      await queryClient.invalidateQueries({ queryKey: ["user"] });
      return {
        id: user.id,
        planTier: user.planTier as NonNullable<PlanTier>,
        email: user.email ?? null,
        stripeSubscriptionId: user.stripeSubscriptionId ?? null,
      };
    }
  }
  return null;
}

const isPlanTier = (value: string | null): value is NonNullable<PlanTier> =>
  !!value && (PLAN_TIERS as string[]).includes(value);

const isBillingInterval = (value: string | null): value is BillingInterval =>
  !!value && (BILLING_INTERVALS as string[]).includes(value);

export const ResumesPage = () => {
  const navigate = useNavigate();
  const [layout, setLayout] = useState<Layout>("grid");
  const [searchParams, setSearchParams] = useSearchParams();
  const { resumes, loading } = useResumes();
  const { open: openImport } = useDialog("import");
  const { user, loading: userLoading } = useUser();
  const openPaywall = useBillingStore((s) => s.openPaywall);
  const billingHandled = useRef(false);
  const paywallHandled = useRef(false);
  const wasWelcomeRef = useRef(false);

  const stayOnDashboard = searchParams.get("stay") === "1";

  useEffect(() => {
    if (searchParams.get("welcome") === "1") wasWelcomeRef.current = true;
  }, [searchParams]);

  useEffect(() => {
    if (loading || userLoading) return;
    if ((resumes?.length ?? 0) === 0 && user?.planTier && !stayOnDashboard) {
      navigate("/onboarding", { replace: true });
    }
  }, [loading, navigate, resumes?.length, stayOnDashboard, user?.planTier, userLoading]);

  useEffect(() => {
    if (billingHandled.current) return;
    const billing = searchParams.get("billing");
    if (!billing) return;

    billingHandled.current = true;

    if (billing === "success") {
      toast({
        variant: "success",
        title: t`Subscription activated!`,
        description: t`Your plan is now active. Enjoy all the features!`,
      });

      const tierParam = searchParams.get("tier");
      const intervalParam = searchParams.get("interval");
      const sessionId = searchParams.get("session_id");
      const startsTrial = searchParams.get("trial") === "1";
      const tier = isPlanTier(tierParam) ? tierParam : null;
      const interval = isBillingInterval(intervalParam) ? intervalParam : null;

      void (async () => {
        const resolved = await pollForActivePlan();
        const finalTier = resolved?.planTier ?? tier;
        const finalInterval = interval ?? "monthly";
        const userId = resolved?.id ?? user?.id ?? null;
        const email = resolved?.email ?? user?.email ?? null;

        if (!finalTier) return;

        trackRegisterSubscribe({
          tier: finalTier,
          interval: finalInterval,
          transactionId: resolved?.stripeSubscriptionId ?? sessionId,
          userId,
          email,
          serverConfirmed: resolved !== null,
          startsTrial,
        });
      })();
    }

    setSearchParams(
      (prev) => {
        prev.delete("billing");
        prev.delete("tier");
        prev.delete("interval");
        prev.delete("session_id");
        prev.delete("trial");
        return prev;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams, user?.id]);

  useEffect(() => {
    if (paywallHandled.current) return;
    if (userLoading) return;
    if (!user) return;
    if (user.planTier) {
      paywallHandled.current = true;
      return;
    }

    const alreadyShown = sessionStorage.getItem(PAYWALL_AUTO_SHOWN_KEY) === "1";
    paywallHandled.current = true;

    if (!alreadyShown) {
      sessionStorage.setItem(PAYWALL_AUTO_SHOWN_KEY, "1");
      const message = wasWelcomeRef.current
        ? t`Welcome to CareerBldr! Start a 3-day trial to build your resume.`
        : t`Subscribe to unlock the resume builder, AI tools, and new exports. Existing resumes can still be downloaded.`;
      openPaywall(undefined, message);
    }
  }, [user, userLoading, openPaywall]);

  const requirePlanToCreate = () => {
    if (user?.planTier) {
      navigate("/onboarding?new=1");
      return;
    }
    openPaywall(undefined, t`Subscribe to a plan to start creating resumes.`);
  };

  const requirePlanToImport = () => {
    if (user?.planTier) {
      openImport("create");
      return;
    }
    openPaywall(undefined, t`Subscribe to a plan to import resumes.`);
  };

  if (!loading && (resumes?.length ?? 0) === 0 && user?.planTier && !stayOnDashboard) {
    return <Navigate to="/onboarding" replace />;
  }

  if (!loading && (resumes?.length ?? 0) === 0) {
    return (
      <>
        <Helmet>
          <title>
            {t`Resumes`} - {t`CareerBldr`}
          </title>
        </Helmet>
        <div className="mx-auto flex min-h-[60vh] max-w-lg flex-col items-start justify-center gap-5">
          <h1 className="text-4xl font-semibold tracking-tight">{t`Your first resume`}</h1>
          <p className="text-muted-foreground text-base leading-relaxed">
            {t`Start a 3-day trial to build your resume with AI, ATS scoring, and exports.`}
          </p>
          <div className="flex flex-wrap gap-3">
            <Button onClick={requirePlanToCreate}>{t`Build my resume`}</Button>
            <Button variant="outline" onClick={requirePlanToImport}>
              <DownloadSimple className="mr-2" />
              {t`Import a file`}
            </Button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          {t`Resumes`} - {t`CareerBldr`}
        </title>
      </Helmet>

      <Tabs
        value={layout}
        onValueChange={(value) => setLayout(value as Layout)}
        className="space-y-4"
      >
        <div className="flex items-center justify-between gap-3">
          <motion.h1
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-4xl font-semibold tracking-tight"
          >
            {t`Resumes`}
          </motion.h1>

          <div className="flex items-center gap-2">
            <Button size="sm" onClick={requirePlanToCreate}>
              {t`Build a resume`}
            </Button>
            <Button variant="ghost" size="sm" onClick={requirePlanToImport}>
              <DownloadSimple className="mr-2" size={16} />
              <span className="hidden sm:inline">{t`Import file`}</span>
            </Button>
            <TabsList>
              <TabsTrigger value="grid" className="h-8 w-8 p-0 sm:h-8 sm:w-auto sm:px-4">
                <SquaresFour />
                <span className="ml-2 hidden sm:block">{t`Grid`}</span>
              </TabsTrigger>
              <TabsTrigger value="list" className="h-8 w-8 p-0 sm:h-8 sm:w-auto sm:px-4">
                <List />
                <span className="ml-2 hidden sm:block">{t`List`}</span>
              </TabsTrigger>
            </TabsList>
          </div>
        </div>

        <ScrollArea className="h-[calc(100vh-140px)] lg:h-[calc(100vh-88px)]">
          <TabsContent value="grid">
            <GridView />
          </TabsContent>
          <TabsContent value="list">
            <ListView />
          </TabsContent>
        </ScrollArea>
      </Tabs>
    </>
  );
};
